import { create } from 'zustand';
import { useEffect, useRef, useCallback } from 'react';
import { useUrlValue } from './urlStateStore';
import { useSearchParams } from 'react-router-dom';

// Define valid view types for Media Gallery
type MediaGalleryView = 'grid' | 'list';

// Media Gallery page state
interface MediaGalleryState {
  searchQuery: string;
  viewType: MediaGalleryView;
  selectedBrandId: string;
  selectedMediaType: string;
  sortOption: string;
  
  // Actions
  setSearchQuery: (query: string) => void;
  setViewType: (view: MediaGalleryView) => void;
  setSelectedBrandId: (brandId: string) => void;
  setSelectedMediaType: (mediaType: string) => void;
  setSortOption: (option: string) => void;
}

// Create the store with default values for Media Gallery
export const useMediaGalleryStore = create<MediaGalleryState>((set) => {
  return {
    searchQuery: '',
    viewType: 'grid', // Default to 'grid' view
    selectedBrandId: 'all',
    selectedMediaType: 'all',
    sortOption: 'newest',
    
    // Actions
    setSearchQuery: (searchQuery) => set({ searchQuery }),
    setViewType: (viewType) => set((state) => {
      if (state.viewType === viewType) return state;
      return { viewType };
    }),
    setSelectedBrandId: (selectedBrandId) => set((state) => {
      if (state.selectedBrandId === selectedBrandId) return state;
      return { selectedBrandId };
    }),
    setSelectedMediaType: (selectedMediaType) => set((state) => {
      if (state.selectedMediaType === selectedMediaType) return state;
      return { selectedMediaType };
    }),
    setSortOption: (sortOption) => set((state) => {
      if (state.sortOption === sortOption) return state;
      return { sortOption };
    }),
  };
});

// Hook to use Media Gallery state with URL synchronization
export function useMediaGalleryUrlState() {
  // Get the raw state and actions from Zustand
  const store = useMediaGalleryStore();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Create refs to track initial values and prevent circular updates
  const isInitialMount = useRef(true);
  const updatingFromStore = useRef(false);
  const storeRef = useRef(store);
  
  // Update store ref
  useEffect(() => {
    storeRef.current = store;
  }, [store]);
  
  // Clean up URL by removing unwanted parameters (optional, can customize if needed)
  useEffect(() => {
    let shouldUpdateParams = false;
    const newParams = new URLSearchParams(searchParams);
    
    // Example: Remove parameters specific to other pages if they appear
    if (newParams.has('platform')) { // From Analytics
      newParams.delete('platform');
      shouldUpdateParams = true;
    }
    if (newParams.has('tab')) { // From Accounts
      newParams.delete('tab');
      shouldUpdateParams = true;
    }
    
    if (shouldUpdateParams) {
      setSearchParams(newParams);
    }
  }, [searchParams, setSearchParams]);
  
  // Connect state values to URL parameters with stable options
  const viewTypeOptions = useRef({ skipSync: false }).current;
  const brandOptions = useRef({ skipSync: false }).current;
  const mediaTypeOptions = useRef({ skipSync: false }).current;
  const sortOptions = useRef({ skipSync: false }).current;
  
  // Connect state values to URL parameters
  const [viewType, setViewType] = useUrlValue<MediaGalleryView>(
    'view', 
    store.viewType,
    viewTypeOptions
  );
  
  const [selectedBrandId, setSelectedBrandId] = useUrlValue<string>(
    'brand',
    store.selectedBrandId,
    brandOptions
  );
  
  const [selectedMediaType, setSelectedMediaType] = useUrlValue<string>(
    'mediaType',
    store.selectedMediaType,
    mediaTypeOptions
  );
  
  const [sortOption, setSortOption] = useUrlValue<string>(
    'sort',
    store.sortOption,
    sortOptions
  );
  
  // On initial mount, sync the store with URL values (URL has priority)
  useEffect(() => {
    if (isInitialMount.current) {
      const currentStore = storeRef.current;
      
      if (viewType !== currentStore.viewType) currentStore.setViewType(viewType);
      if (selectedBrandId !== currentStore.selectedBrandId) currentStore.setSelectedBrandId(selectedBrandId);
      if (selectedMediaType !== currentStore.selectedMediaType) currentStore.setSelectedMediaType(selectedMediaType);
      if (sortOption !== currentStore.sortOption) currentStore.setSortOption(sortOption);
      
      isInitialMount.current = false;
    }
  }, []); // Empty dependency array ensures this runs only once
  
  // Create wrapper functions for setters that update both URL and store
  const setViewTypeWrapper = useCallback((newView: MediaGalleryView) => {
    if (newView === viewType) return;
    
    setViewType(newView);
    updatingFromStore.current = true;
    storeRef.current.setViewType(newView);
    updatingFromStore.current = false;
  }, [viewType, setViewType]);
  
  const setSelectedBrandIdWrapper = useCallback((newBrand: string) => {
    if (newBrand === selectedBrandId) return;
    
    setSelectedBrandId(newBrand);
    updatingFromStore.current = true;
    storeRef.current.setSelectedBrandId(newBrand);
    updatingFromStore.current = false;
  }, [selectedBrandId, setSelectedBrandId]);
  
  const setSelectedMediaTypeWrapper = useCallback((newMediaType: string) => {
    if (newMediaType === selectedMediaType) return;
    
    setSelectedMediaType(newMediaType);
    updatingFromStore.current = true;
    storeRef.current.setSelectedMediaType(newMediaType);
    updatingFromStore.current = false;
  }, [selectedMediaType, setSelectedMediaType]);
  
  const setSortOptionWrapper = useCallback((newSortOption: string) => {
    if (newSortOption === sortOption) return;
    
    setSortOption(newSortOption);
    updatingFromStore.current = true;
    storeRef.current.setSortOption(newSortOption);
    updatingFromStore.current = false;
  }, [sortOption, setSortOption]);
  
  // Keep Zustand store in sync with URL changes
  useEffect(() => {
    if (!isInitialMount.current && !updatingFromStore.current && viewType !== storeRef.current.viewType) {
      storeRef.current.setViewType(viewType);
    }
  }, [viewType]);
  
  useEffect(() => {
    if (!isInitialMount.current && !updatingFromStore.current && selectedBrandId !== storeRef.current.selectedBrandId) {
      storeRef.current.setSelectedBrandId(selectedBrandId);
    }
  }, [selectedBrandId]);
  
  useEffect(() => {
    if (!isInitialMount.current && !updatingFromStore.current && selectedMediaType !== storeRef.current.selectedMediaType) {
      storeRef.current.setSelectedMediaType(selectedMediaType);
    }
  }, [selectedMediaType]);
  
  useEffect(() => {
    if (!isInitialMount.current && !updatingFromStore.current && sortOption !== storeRef.current.sortOption) {
      storeRef.current.setSortOption(sortOption);
    }
  }, [sortOption]);
  
  // Return URL-synced values and our wrapped setters
  return {
    // URL synced values
    viewType,
    selectedBrandId,
    selectedMediaType,
    sortOption,
    
    // URL synced setters (using our wrapper functions)
    setViewType: setViewTypeWrapper,
    setSelectedBrandId: setSelectedBrandIdWrapper,
    setSelectedMediaType: setSelectedMediaTypeWrapper,
    setSortOption: setSortOptionWrapper,
    
    // Values not synced with URL
    searchQuery: store.searchQuery,
    
    // Setters not synced with URL
    setSearchQuery: store.setSearchQuery
  };
} 